import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

export const createValueStore = (key) => {
  const get = () => {
    return window.localStorage.getItem(key);
  }

  const set = (value) => {
    window.localStorage.setItem(key, value);
    window.dispatchEvent(new Event('storage:internal'));
  }

  const remove = () => {
    window.localStorage.removeItem(key);
    window.dispatchEvent(new Event('storage:internal'));
  }

  const subscribe = (callback) => {
    window.addEventListener('storage', callback);
    window.addEventListener('storage:internal', callback);

    return () => {
      window.removeEventListener('storage', callback);
      window.removeEventListener('storage:internal', callback);
    }
  }

  return {
    get,
    set,
    remove,
    subscribe,
  }
}

export const useLocalStorage = (
  key, 
  initialState, 
  options={ parser: JSON.parse, serializer: JSON.stringify }
) => {
  const parser = useCallback(options.parser, []);
  const serializer = useCallback(options.serializer, []);
  
  const initialStateRef = useRef(initialState);
  
  const getSnapshot = useCallback(() => {
    const value = window.localStorage.getItem(key);
    const invalidValue = value === 'undefined' || value === 'null' || !value
    const snapshot = invalidValue ? initialStateRef.current : parser(value);

    return snapshot;
  }, [key, parser]);

  const [value, setValue] = useState(getSnapshot());
  const valueRef = useRef(value);


  const updateValue = useCallback((value) => {
    window.localStorage.setItem(key, serializer(value));
    window.dispatchEvent(new StorageEvent('storage:internal', { key }));
  }, [key, serializer])

  const removeValue = useCallback(() => {
    window.localStorage.removeItem(key);
    window.dispatchEvent(new StorageEvent('storage:internal', { key }));
  }, [key])

  const onStorageUpdate = useCallback((e) => {
    if (e.key !== key) return;

    const snapshot = getSnapshot();
    valueRef.current = snapshot;

    setValue(snapshot);
  }, [key, getSnapshot])

  useEffect(() => {
    const store = createValueStore(key);
    const unsubscribe = store.subscribe(onStorageUpdate);

    return unsubscribe;
  }, [key, onStorageUpdate])

  return [
    value,
    updateValue,
    removeValue,
    valueRef
  ]
}
